import { capitalDesignTokens } from "@opengov/capital-design-tokens";
const MuiInputAdornment = {
  styleOverrides: {
    root: {
      margin: 0
    }
  }
};
const MuiOutlinedInput = {
  styleOverrides: {
    root: ({ ownerState, theme }) => {
      var _a;
      return {
        backgroundColor: theme.palette.background.paper,
        "&.MuiInputBase-colorSuccess .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.success.main
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
          borderWidth: "2px"
        },
        "&.Mui-disabled, &.Mui-disabled:hover": {
          backgroundColor: theme.palette.action.disabledBackground,
          borderRadius: capitalDesignTokens.foundations.units.unitHalf
        },
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline, &.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.divider,
          borderRadius: capitalDesignTokens.foundations.units.unitHalf
        },
        // MUI's base style already moves the hover border to `text.primary` for the default colour;
        // this re-asserts it for the other colours, whose base rule would otherwise win.
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: ownerState.color !== "primary" && theme.palette.text.primary,
          borderRadius: capitalDesignTokens.foundations.units.unitHalf
        },
        // On touch devices MUI's base style drops the hover border back to a 23% grey. Keep the
        // rest-state colour instead so a tapped (sticky-hover) field never falls under 3:1. Only the
        // default colour is affected (other colours already hold `text.primary` from the rule above),
        // and the state rules keep their own colour: this selector sits after them and would win.
        "@media (hover: none)": {
          "&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled):not(.MuiInputBase-colorSuccess) .MuiOutlinedInput-notchedOutline": {
            borderColor: ownerState.color === "primary" && ((_a = theme.palette.outline) != null ? _a : theme.palette.divider)
          }
        },
        "&.Mui-focusVisible, &:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2
        }
      };
    },
    adornedStart: ({ theme }) => ({
      paddingLeft: theme.spacing(1.5),
      "& .MuiInputBase-input": {
        paddingLeft: theme.spacing(1)
      },
      ".MuiSvgIcon-root": {
        fontSize: theme.typography.bodyDefault.fontSize,
        "&.MuiSelect-icon": {
          fontSize: "1.5rem"
        }
      },
      ".MuiButtonBase-root": {
        padding: 0
      }
    }),
    adornedEnd: ({ theme }) => ({
      paddingRight: theme.spacing(1),
      ".MuiButtonBase-root": {
        margin: theme.spacing(-1),
        "&:hover": {
          backgroundColor: "transparent",
          color: theme.palette.text.primary
        }
      }
    }),
    input: ({ theme, ownerState }) => ({
      fontWeight: 400,
      lineHeight: 1,
      // Fill the container height exactly so the input is the same height as buttons/toggles.
      // boxSizing: border-box means padding is included within the 100% height.
      height: "100%",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      // Horizontal padding only — vertical centering is handled by height: 100%
      padding: theme.spacing(0, 2),
      ...ownerState.size === "small" && {
        padding: theme.spacing(0, 1.5)
      },
      ...ownerState.size === "large" && {
        padding: theme.spacing(0, 2)
      },
      color: theme.palette.text.primary,
      "&::-webkit-input-placeholder, &::-moz-placeholder, &::placeholder": {
        WebkitTextFillColor: theme.palette.text.secondary,
        opacity: 1
      },
      "&.Mui-disabled::-webkit-input-placeholder, &.Mui-disabled::-moz-placeholder, &.Mui-disabled::placeholder": {
        WebkitTextFillColor: theme.palette.text.disabled
      },
      label: {
        color: theme.palette.text.primary
      }
    }),
    multiline: ({ theme }) => ({
      padding: theme.spacing(1),
      height: "auto"
    }),
    inputMultiline: {
      padding: 0,
      lineHeight: "1.25rem",
      height: "auto"
    },
    notchedOutline: ({ theme }) => {
      var _a;
      return {
        // A control boundary must reach 3:1 (WCAG 1.4.11). `divider` is a decorative separator and
        // sits at ~1.3:1, so it stays on the disabled state only, which that rule exempts.
        borderColor: (_a = theme.palette.outline) != null ? _a : theme.palette.divider,
        legend: {
          width: 0
        }
      };
    }
  }
};
const MuiInputBase = {
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      "label+&&": {
        margin: 0
      },
      height: `${capitalDesignTokens.componentSizes.medium}px`,
      minHeight: `${capitalDesignTokens.componentSizes.medium}px`,
      ...ownerState.size === "small" && {
        height: `${capitalDesignTokens.componentSizes.small}px`,
        minHeight: `${capitalDesignTokens.componentSizes.small}px`
      },
      ...ownerState.size === "large" && {
        height: `${capitalDesignTokens.componentSizes.large}px`,
        minHeight: `${capitalDesignTokens.componentSizes.large}px`
      },
      ...ownerState.multiline && {
        height: "auto"
      },
      "& .MuiSelect-icon": {
        right: theme.spacing(1),
        fontSize: "1.5rem"
      }
    }),
    input: ({ ownerState }) => ({
      fontSize: capitalDesignTokens.componentFontSizes.medium,
      "&::placeholder": {
        fontSize: capitalDesignTokens.componentFontSizes.medium
      },
      ...ownerState.size === "small" && {
        fontSize: capitalDesignTokens.componentFontSizes.small
      },
      ...ownerState.size === "large" && {
        fontSize: capitalDesignTokens.componentFontSizes.large
      }
    })
  }
};
const MuiFilledInput = {
  styleOverrides: {
    input: ({ theme, ownerState }) => ({
      padding: theme.spacing(1, 1.5),
      ...ownerState.size === "small" && {
        padding: theme.spacing(0.25, 1)
      },
      ...ownerState.size === "large" && {
        padding: theme.spacing(0.675, 2)
      }
    })
  }
};
const MuiFormHelperText = {
  styleOverrides: {
    root: ({ theme }) => ({
      marginLeft: 0,
      fontSize: theme.typography.bodySmall.fontSize,
      lineHeight: "20px",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      "& .MuiSvgIcon-root": {
        width: theme.spacing(2),
        height: theme.spacing(2)
      }
    })
  }
};
export {
  MuiFilledInput,
  MuiFormHelperText,
  MuiInputAdornment,
  MuiInputBase,
  MuiOutlinedInput
};
//# sourceMappingURL=MuiInput.js.map
